import { Box } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Emission } from "../../db_access/emissions";
import { Emission_detail } from "../../db_access/emission_details";
import { Supply_chain } from "../../db_access/supply_chain";
import EmissionDetailGrid from "./Scope/EmissionDetailGrid";
import EmissionGrid from "./Scope/EmissionGrid";
import { allCategories, allCategoriesForSupplyChain, scope1Categories, scope2Categories, scope3Categories, scope3CategoriesForSupplyChain } from "./ScopeCategory";

function OrganizationScope({ organizationID, organizationType, emissions, emissionDetails, readOnly = false, validationStatus = "Not Validated" }) {
  const { t } = useTranslation();
  const { isSaveClicked, setIsSaveClicked, setIsSaveVisible, setIsLoading, setRefresh, enqueueAlert } = useContextProvider();
  const [selectedEmissionIDs, setSelectedEmissionIDs] = useState([]);
  const [inventoryTotalFossil, setInventoryTotalFossil] = useState(0);
  const [inventoryTotalBiogenic, setInventoryTotalBiogenic] = useState(0);
  const [hasPendingDetailEdits, setHasPendingDetailEdits] = useState(false);

  const emissionGridSaveRef = useRef(null);
  const emissionDetailGridSaveRef = useRef(null);

  const includesCategory = (categories, cat) => {
    if (!cat) return false;
    const normalized = cat.toLowerCase().trim();
    return categories.some(c => c.toLowerCase().trim() === normalized);
  };

  const all = useMemo(
    () => ({ label: "All", value: "All", filter: (item) => true, categories: organizationType === Supply_chain.label ? allCategoriesForSupplyChain : allCategories }),
    [organizationType]
  );
  const scope1 = useMemo(() => ({ label: "Scope 1", value: "Scope 1", filter: (item) => !item.is_avoided && !item.is_biogenic && (includesCategory(scope1Categories, item.category) || (!item.category && item._scopeTab === "Scope 1")), categories: scope1Categories }), []);
  const scope2 = useMemo(() => ({ label: "Scope 2", value: "Scope 2", filter: (item) => !item.is_avoided && !item.is_biogenic && (includesCategory(scope2Categories, item.category) || (!item.category && item._scopeTab === "Scope 2")), categories: scope2Categories }), []);
  const scope3 = useMemo(
    () => ({
      label: "Scope 3",
      value: "Scope 3",
      filter: (item) => !item.is_avoided && !item.is_biogenic && (includesCategory(organizationType === Supply_chain.label ? scope3CategoriesForSupplyChain : scope3Categories, item.category) || (!item.category && item._scopeTab === "Scope 3")),
      categories: organizationType === Supply_chain.label ? scope3CategoriesForSupplyChain : scope3Categories,
    }),
    [organizationType]
  );
  const avoided = useMemo(() => ({ label: "Avoided", value: "Avoided", filter: (item) => !!item.is_avoided, categories: allCategoriesForSupplyChain }), []);
  const biogenic = useMemo(() => ({ label: "Biogenic", value: "Biogenic", filter: (item) => !!item.is_biogenic, categories: allCategoriesForSupplyChain }), []);
  const tabs = useMemo(() => [all, scope1, scope2, scope3, avoided /*, biogenic */], [all, scope1, scope2, scope3, avoided, biogenic]);
  const [selectedTab, setSelectedTab] = useState(all.value);

  const filteredEmissions = useMemo(() => {
    const tab = tabs.find((tab) => tab.value === selectedTab);
    const list = tab ? emissions.filter(tab.filter) : emissions;

    if (!emissionDetails || emissionDetails.length === 0) return list;

    const detailsMap = {};
    const biogenicMap = {};
    emissionDetails.forEach((d) => {
      if (!d.emission_id) return;
      detailsMap[d.emission_id] = (detailsMap[d.emission_id] || 0) + (Number(d.totalco2eq) || 0);
      biogenicMap[d.emission_id] = (biogenicMap[d.emission_id] || 0) + (Number(d.biogenic_total) || 0);
    });

    return list.map((e) => {
      const detailFossil = detailsMap[e.id];
      const detailBiogenic = biogenicMap[e.id];
      if (detailFossil !== undefined && detailFossil > 0 && Math.abs((Number(e.gwp) || 0) - detailFossil) > 0.01) {
        return {
          ...e,
          gwp: detailFossil,
          biogenic_gwp: detailBiogenic !== undefined ? detailBiogenic : e.biogenic_gwp,
          _isModified: true,
        };
      }
      return e;
    });
  }, [selectedTab, emissions, tabs, emissionDetails]);

  const emissionCategories = useMemo(() => {
    if (tabs) {
      const tab = tabs.find((tab) => tab.value === selectedTab);
      return tab ? tab.categories : [];
    }
    return [];
  }, [selectedTab, tabs]);

  const selectedEmissionID = selectedEmissionIDs.length === 1 ? selectedEmissionIDs[0] : null;

  useEffect(() => {
    setInventoryTotalFossil(0);
    setInventoryTotalBiogenic(0);
    setHasPendingDetailEdits(false);
  }, [selectedEmissionID]);

  const selectedEmissions = useMemo(
    () => emissions.filter(e => selectedEmissionIDs.includes(e.id)),
    [emissions, selectedEmissionIDs]
  );

  const selectedEmissionCategory = selectedEmissionIDs.length === 1 ? (selectedEmissions[0]?.category ?? null) : null;
  const isBiogenic = selectedEmissionIDs.length === 1 ? (selectedEmissions[0]?.is_biogenic || false) : false;
  const isAvoided = selectedEmissionIDs.length === 1 ? (selectedEmissions[0]?.is_avoided || false) : false;

  const filteredEmissionDetails = useMemo(
    () => selectedEmissionIDs.length > 0 ? emissionDetails.filter(ed => selectedEmissionIDs.includes(ed.emission_id)) : [],
    [selectedEmissionIDs, emissionDetails]
  );

  // Coordinated Save Orchestration: eliminates race conditions and ensures clean transactional saves
  useEffect(() => {
    if (!isSaveClicked) return;

    emissionGridSaveRef.current?.stopEditing?.();
    emissionDetailGridSaveRef.current?.stopEditing?.();

    const emissionPayload = emissionGridSaveRef.current?.getSavePayload?.() || { toSave: [], toDelete: [] };
    const detailPayload = emissionDetailGridSaveRef.current?.getSavePayload?.() || { toSave: [], toDelete: [] };

    const hasEmissionChanges = emissionPayload.toSave.length > 0 || emissionPayload.toDelete.length > 0;
    const hasDetailChanges = detailPayload.toSave.length > 0 || detailPayload.toDelete.length > 0;

    if (!hasEmissionChanges && !hasDetailChanges) {
      setIsSaveClicked(false);
      setIsLoading(false);
      setIsSaveVisible(false);
      return;
    }

    setIsLoading(true);

    const executeSave = async () => {
      // 1. Save parent emissions first (ensures foreign key exists for new details)
      if (emissionPayload.toSave.length > 0) {
        const res = await Emission.save(emissionPayload.toSave);
        if (!res.ok) throw new Error("Failed to save emissions");
      }

      // 2. Save emission details
      if (detailPayload.toSave.length > 0) {
        const res = await Emission_detail.save(detailPayload.toSave);
        if (!res.ok) throw new Error("Failed to save emission details");
      }

      // 3. Delete child details first
      if (detailPayload.toDelete.length > 0) {
        const res = await Emission_detail.delete(detailPayload.toDelete);
        if (!res.ok) throw new Error("Failed to delete emission details");
      }

      // 4. Delete parent emissions
      if (emissionPayload.toDelete.length > 0) {
        const res = await Emission.delete(emissionPayload.toDelete);
        if (!res.ok) throw new Error("Failed to delete emissions");
      }
    };

    executeSave()
      .then(() => {
        emissionGridSaveRef.current?.onSaveSuccess?.(emissionPayload);
        emissionDetailGridSaveRef.current?.onSaveSuccess?.(detailPayload);
        setIsSaveVisible(false);
        setRefresh((r) => !r);
        enqueueAlert?.({ message: t("Changes saved successfully"), severity: "success" });
      })
      .catch((err) => {
        console.error("Error saving organization emissions:", err);
        enqueueAlert?.({ message: t("Failed to save changes"), severity: "error" });
      })
      .finally(() => {
        setIsSaveClicked(false);
        setIsLoading(false);
      });
  }, [isSaveClicked, setIsSaveClicked, setIsSaveVisible, setIsLoading, setRefresh, enqueueAlert, t]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <EmissionGrid
        organizationType={organizationType}
        organizationID={organizationID}
        emissions={filteredEmissions}
        emissionCategories={emissionCategories}
        selectedEmissionID={selectedEmissionID}
        setSelectedEmissionIDs={setSelectedEmissionIDs}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        tabs={tabs}
        inventoryTotalFossil={inventoryTotalFossil}
        inventoryTotalBiogenic={inventoryTotalBiogenic}
        hasPendingDetailEdits={hasPendingDetailEdits}
        saveRef={emissionGridSaveRef}
        readOnly={readOnly}
        validationStatus={validationStatus}
      />

      <EmissionDetailGrid
        organizationType={organizationType}
        emissionID={selectedEmissionID}
        emissionCategory={selectedEmissionCategory}
        emissionDetails={filteredEmissionDetails}
        isBiogenic={isBiogenic}
        isAvoided={isAvoided}
        onInventoryTotalChange={setInventoryTotalFossil}
        onInventoryBiogenicTotalChange={setInventoryTotalBiogenic}
        onPendingEditsChange={setHasPendingDetailEdits}
        saveRef={emissionDetailGridSaveRef}
        readOnly={readOnly}
        validationStatus={validationStatus}
      />
    </Box>
  );
}

export default OrganizationScope;
